import classNames from 'classnames';
import {
  animate,
  AnimatePresence,
  cubicBezier,
  motion,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from 'motion/react';
import { Button, ButtonProps } from 'react-aria-components/Button';
import { Dialog, Heading as DialogHeading } from 'react-aria-components/Dialog';
import { Modal, ModalOverlay } from 'react-aria-components/Modal';

import Icon from '../Icon/Icon';
import Heading, { HeadingSize } from '../Typography/Heading/Heading';
import css from './Sheet.css';

const MotionModal = motion.create(Modal);
const MotionModalOverlay = motion.create(ModalOverlay);

const inertiaTransition = {
  type: 'inertia' as const,
  bounceStiffness: 300,
  bounceDamping: 40,
  timeConstant: 300,
};

const staticTransition = {
  duration: 0.5,
  ease: cubicBezier(0.32, 0.72, 0, 1),
};

const SHEET_MARGIN = 80;

interface SheetTriggerProps extends Pick<ButtonProps, 'aria-label' | 'children' | 'className'> {
  onOpenChange: (isOpen: boolean) => void;
}

export function SheetTrigger({ onOpenChange, ...props }: SheetTriggerProps) {
  return <Button onPress={() => onOpenChange(true)} {...props} />;
}

interface SheetProps {
  children: React.ReactNode;
  closeButtonLabel: string;
  title: React.ReactNode;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  className?: string;
}

export function Sheet({ children, className, closeButtonLabel, isOpen, onOpenChange, title }: SheetProps) {
  const height = typeof window !== 'undefined' ? window.innerHeight - SHEET_MARGIN : 0;
  const y = useMotionValue(height);
  const backgroundOpacity = useTransform(y, [0, height], [0.4, 0]);
  const backgroundColor = useMotionTemplate`rgba(0, 0, 0, ${backgroundOpacity})`;

  return (
    <AnimatePresence>
      {isOpen && (
        <MotionModalOverlay
          className={css.overlay}
          isDismissable
          // Force the modal to be open when AnimatePresence renders it.
          isOpen
          onOpenChange={onOpenChange}
          style={{ backgroundColor }}
        >
          <MotionModal
            className={css.modal}
            initial={{ y: height }}
            animate={{ y: 0 }}
            exit={{ y: height }}
            transition={staticTransition}
            style={{
              y,
              top: SHEET_MARGIN,
              // Extra padding at the bottom to account for rubber band scrolling.
              paddingBottom: typeof window !== 'undefined' ? window.screen.height : 0,
            }}
            drag="y"
            dragConstraints={{ top: 0 }}
            onDragEnd={(e, { offset, velocity }) => {
              if (offset.y > window.innerHeight * 0.75 || velocity.y > 10) {
                onOpenChange(false);
              } else {
                animate(y, 0, { ...inertiaTransition, min: 0, max: 0 });
              }
            }}
          >
            <div className={css.inner} style={{ height: `calc(var(--visual-viewport-height) - ${SHEET_MARGIN}px)` }}>
              <div className={css.handle} />
              <Dialog className={css.dialog}>
                <div className={css.header}>
                  <DialogHeading
                    slot="title"
                    render={(props) => (
                      <Heading
                        {...props}
                        className={classNames(css.heading, props.className)}
                        level={2}
                        size={HeadingSize.Small}
                      >
                        {title}
                      </Heading>
                    )}
                  />
                  <Button className={css.buttonClose} onPress={() => onOpenChange(false)} aria-label={closeButtonLabel}>
                    <Icon className={css.icon} name="cross" />
                  </Button>
                </div>
                <div className={classNames(css.content, className)}>{children}</div>
              </Dialog>
            </div>
          </MotionModal>
        </MotionModalOverlay>
      )}
    </AnimatePresence>
  );
}
